import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ArrowLeft, Plus, Trash2, AlertTriangle, CheckCircle2, RefreshCw } from "lucide-react";
import VideoEmbed, { detectPlatform } from "@/components/VideoEmbed";

const PILLARS = [
  { key: "walking",     label: "Walking",      colour: "#E65322" },
  { key: "lifting",     label: "Lifting",      colour: "#3B82F6" },
  { key: "whole_foods", label: "Whole Foods",  colour: "#10B981" },
  { key: "recovery",    label: "Recovery",     colour: "#7C3AED" },
];

export default function AdminSiteVideos() {
  const [videos, setVideos] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/admin/site/videos");
      setVideos(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, []);

  const addVideo = async (pillar) => {
    setBusy(true);
    try {
      await api.post("/admin/site/videos", { pillar, url: "", title: "", creator: "", credibility: "" });
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const update = async (id, patch) => {
    try {
      await api.patch(`/admin/site/videos/${id}`, patch);
      await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const recheck = async () => {
    setBusy(true);
    try { await api.post("/admin/site/videos/recheck"); await load(); toast.success("Embed status refreshed"); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const broken = videos.filter((v) => v.url && v.embeddable === false);

  const remove = async (id) => {
    if (!confirm("Remove this video?")) return;
    try { await api.delete(`/admin/site/videos/${id}`); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-8" data-testid="admin-site-videos-root">
      <div>
        <Link to="/app/admin" className="text-xs uppercase tracking-widest text-muted-foreground inline-flex items-center gap-1 hover:text-foreground">
          <ArrowLeft size={14} /> Admin
        </Link>
        <div className="label-eyebrow mt-4">Homepage CMS</div>
        <h1 className="wordmark mt-3 text-4xl md:text-5xl uppercase">Our System Works · Videos</h1>
        <p className="mt-3 text-sm text-muted-foreground max-w-2xl">
          Manage the credible-source video reels shown on the public homepage. YouTube, YouTube Shorts and TikTok work out of the box.
          Paste the full URL — the platform is auto-detected. Leave URL blank to hide a slot.
        </p>
      </div>

      <div className={`rounded-lg border p-4 flex items-start gap-3 ${broken.length ? "border-destructive/40 bg-destructive/5" : "border-emerald-500/30 bg-emerald-500/5"}`} data-testid="video-health-banner">
        {broken.length ? <AlertTriangle size={18} className="text-destructive shrink-0 mt-0.5" /> : <CheckCircle2 size={18} className="text-emerald-600 shrink-0 mt-0.5" />}
        <div className="flex-1 text-sm">
          {broken.length ? (
            <>
              <div className="font-head uppercase tracking-wider text-destructive">{broken.length} video{broken.length === 1 ? "" : "s"} can't be embedded</div>
              <p className="text-xs text-muted-foreground mt-1">The creator has disabled embedding, or the video was removed. Visitors see a "Watch on TikTok" card instead of the player. Replace the URL to fix.</p>
              <ul className="mt-2 space-y-1 text-xs">
                {broken.map((v) => <li key={v.id} data-testid={`video-health-item-${v.id}`}>• <b>{PILLARS.find((p) => p.key === v.pillar)?.label}</b> — {v.title || v.url}</li>)}
              </ul>
            </>
          ) : (
            <div className="font-head uppercase tracking-wider text-emerald-700">All TikTok embeds healthy</div>
          )}
        </div>
        <button onClick={recheck} disabled={busy} data-testid="video-health-recheck"
          className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest border border-border px-3 py-2 rounded hover:bg-muted disabled:opacity-60">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Re-check
        </button>
      </div>

      {PILLARS.map((p) => {
        const list = videos.filter((v) => v.pillar === p.key).sort((a, b) => (a.order || 0) - (b.order || 0));
        return (
          <div key={p.key} className="bg-card border border-border rounded-lg p-6" data-testid={`admin-videos-pillar-${p.key}`}>
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="label-eyebrow" style={{ color: p.colour }}>Pillar</div>
                <h2 className="wordmark mt-1 text-2xl uppercase">{p.label}</h2>
                <div className="text-[11px] uppercase tracking-widest text-muted-foreground mt-1">{list.length} video{list.length === 1 ? "" : "s"}</div>
              </div>
              <button onClick={() => addVideo(p.key)} disabled={busy}
                className="inline-flex items-center gap-2 bg-foreground text-background text-[11px] uppercase tracking-widest px-3 py-2 rounded hover:opacity-90 disabled:opacity-60"
                data-testid={`admin-add-video-${p.key}`}>
                <Plus size={14} /> Add video
              </button>
            </div>

            <div className="mt-5 grid md:grid-cols-3 gap-5">
              {list.map((v) => (
                <VideoRow key={v.id} video={v} onUpdate={(patch) => update(v.id, patch)} onRemove={() => remove(v.id)} pillarColour={p.colour} />
              ))}
              {list.length === 0 && (
                <div className="rounded-md border border-dashed border-border p-6 text-center text-xs uppercase tracking-widest text-muted-foreground col-span-full">
                  No videos yet · click "Add video" to start
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function VideoRow({ video, onUpdate, onRemove, pillarColour }) {
  const [form, setForm] = useState({ url: video.url || "", title: video.title || "", creator: video.creator || "", credibility: video.credibility || "", order: video.order || 0 });
  const platform = detectPlatform(form.url);
  const save = () => onUpdate(form);
  return (
    <div className={`rounded-md border p-4 bg-background ${video.embeddable === false ? "border-destructive" : "border-border"}`} data-testid={`admin-video-row-${video.id}`}>
      {video.embeddable === false && (
        <div className="mb-3 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest text-destructive" data-testid={`video-not-embeddable-${video.id}`}>
          <AlertTriangle size={12} /> Not embeddable — replace URL
        </div>
      )}
      <VideoEmbed url={form.url} thumbnail={video.thumbnail} embeddable={video.embeddable} className="mb-3" />
      <div className="space-y-2">
        <label className="text-[10px] uppercase tracking-widest text-muted-foreground">URL</label>
        <input value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })} onBlur={save}
          placeholder="https://www.youtube.com/shorts/... or https://www.tiktok.com/@user/video/..."
          className="w-full rounded-md border border-input bg-card px-3 py-2 text-xs" data-testid={`video-url-${video.id}`} />
        <div className="text-[10px] uppercase tracking-widest text-muted-foreground">Platform · <span style={{ color: pillarColour }}>{platform}</span></div>

        <label className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2 block">Title (short)</label>
        <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} onBlur={save}
          placeholder="e.g. Walking beats cardio for fat loss"
          className="w-full rounded-md border border-input bg-card px-3 py-2 text-xs" data-testid={`video-title-${video.id}`} />

        <label className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2 block">Creator / handle</label>
        <input value={form.creator} onChange={(e) => setForm({ ...form, creator: e.target.value })} onBlur={save}
          placeholder="e.g. Dr. Layne Norton"
          className="w-full rounded-md border border-input bg-card px-3 py-2 text-xs" data-testid={`video-creator-${video.id}`} />

        <label className="text-[10px] uppercase tracking-widest text-muted-foreground mt-2 block">Credibility (why they matter)</label>
        <input value={form.credibility} onChange={(e) => setForm({ ...form, credibility: e.target.value })} onBlur={save}
          placeholder="e.g. PhD Nutritional Sciences · pro natural bodybuilder"
          className="w-full rounded-md border border-input bg-card px-3 py-2 text-xs" data-testid={`video-credibility-${video.id}`} />

        <div className="flex items-center justify-between gap-2 mt-3">
          <div className="flex items-center gap-2">
            <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Order</span>
            <input type="number" value={form.order} onChange={(e) => setForm({ ...form, order: parseInt(e.target.value || 0, 10) })} onBlur={save}
              className="w-16 rounded-md border border-input bg-card px-2 py-1 text-xs" data-testid={`video-order-${video.id}`} />
          </div>
          <button onClick={onRemove} className="inline-flex items-center gap-1 text-[11px] uppercase tracking-widest text-destructive hover:text-destructive/80" data-testid={`video-remove-${video.id}`}>
            <Trash2 size={12} /> Remove
          </button>
        </div>
      </div>
    </div>
  );
}
